import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useAnimationControls } from "framer-motion";
import type { AuthInputProps } from "../auth.types";

// ── Main component ───────────────────────────────────────────────────────────
export const AuthInput = ({
  id,
  name,
  type = "text",
  value,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  error,
  disabled = false,
  autoComplete,
  "aria-label": ariaLabel,
}: AuthInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [scanKey, setScanKey] = useState(0);
  const [showScan, setShowScan] = useState(false);
  const [typingKey, setTypingKey] = useState(0);
  const [showTypingFlash, setShowTypingFlash] = useState(false);

  const shakeControls = useAnimationControls();
  const prevError = useRef(error);
  const isFocusedRef = useRef(false); // stable ref for effects
  const isFirstRender = useRef(true);

  const isEmptyInput = !value;
  const hasError = !!error;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleFocus = () => {
    isFocusedRef.current = true;
    setIsFocused(true);
    setScanKey((k) => k + 1);
    setShowScan(true);
    setTimeout(() => setShowScan(false), 650);
    onFocus?.();
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    setIsFocused(false);
    onBlur?.();
  };

  // ── Shake on new error ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!prevError.current && error) {
      shakeControls.start({
        x: [0, -9, 9, -6, 6, -3, 3, 0],
        transition: { duration: 0.45, ease: "easeOut" },
      });
    }
    prevError.current = error;
  }, [error, shakeControls]);

  // ── Per-keystroke trailing flash ───────────────────────────────────────────
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isFocusedRef.current) return;
    setTypingKey((k) => k + 1);
    setShowTypingFlash(true);
    const t = setTimeout(() => setShowTypingFlash(false), 220);
    return () => clearTimeout(t);
  }, [value]);

  // ── Derived border color ───────────────────────────────────────────────────
  const borderColor = hasError
    ? "#ef4444"
    : isFocused
      ? (["#48A7FF", "#85caff", "#48A7FF"] as unknown as string) // keyframe while focused
      : !isEmptyInput
        ? "#48A7FF"
        : "#697682";

  return (
    <div className="flex flex-col gap-1">
      <motion.div animate={shakeControls} className="relative">
        {/* ── Outer glow ring ─────────────────────────────────────────────── */}
        <motion.div
          animate={{
            boxShadow: hasError
              ? "0 0 0 1px rgba(239,68,68,0.75), 0 0 16px rgba(239,68,68,0.28)"
              : showScan
                ? [
                    "0 0 0 1px #48A7FF, 0 0 6px rgba(72,167,255,0.15)",
                    "0 0 0 1px #48A7FF, 0 0 24px rgba(72,167,255,0.55)",
                    "0 0 0 1px #48A7FF, 0 0 10px rgba(72,167,255,0.22)",
                  ]
                : isFocused
                  ? "0 0 0 1px #48A7FF, 0 0 10px rgba(72,167,255,0.22)"
                  : "0 0 0 1px transparent",
          }}
          transition={
            showScan && !hasError
              ? { duration: 0.6, times: [0, 0.3, 1], ease: "easeOut" }
              : { duration: 0.3 }
          }
          className="rounded-[8px] overflow-hidden relative"
        >
          {/* Scan line — fires once on focus */}
          <AnimatePresence>
            {showScan && (
              <motion.span
                key={`scan-${scanKey}`}
                className="absolute inset-y-0 w-[55%] pointer-events-none z-10"
                style={{
                  background:
                    "linear-gradient(to right, transparent, rgba(72,167,255,0.22), transparent)",
                }}
                initial={{ left: "-55%" }}
                animate={{ left: "155%" }}
                transition={{ duration: 0.55, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>

          {/* Per-keystroke trailing flash on the right edge */}
          <AnimatePresence>
            {showTypingFlash && (
              <motion.span
                key={`type-${typingKey}`}
                className="absolute inset-y-0 right-0 w-[45%] pointer-events-none z-10"
                style={{
                  background:
                    "linear-gradient(to left, rgba(72,167,255,0.2), transparent)",
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: 0.22, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>

          {/* ── Input ─────────────────────────────────────────────────────── */}
          <motion.input
            id={id}
            name={name}
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            autoComplete={autoComplete}
            aria-label={ariaLabel ?? name}
            aria-invalid={hasError}
            // Animate left border: breathing pulse while focused, steady otherwise
            animate={{ borderLeftColor: borderColor }}
            transition={
              isFocused && !hasError
                ? { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
                : { duration: 0.3 }
            }
            className={[
              "font-[500] outline-none w-full p-5 px-5 rounded-[8px] border-l-[6px] bg-transparent bg-gradient-to-r",
              hasError
                ? "text-red-300 from-[#3a1a1a] via-[#3a1a1a]/40 to-transparent"
                : isEmptyInput
                  ? "text-white/30 from-[#394B59] via-[#394B59]/40 to-transparent"
                  : "text-[#48A7FF] from-[#1a4061] via-[#1a4061]/50 to-transparent",
              disabled ? "opacity-50 cursor-not-allowed" : "cursor-text",
            ]
              .filter(Boolean)
              .join(" ")}
          />
        </motion.div>
      </motion.div>

      {/* ── Error message ──────────────────────────────────────────────────── */}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -5, x: -3 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22, ease: "backOut" }}
            className="text-xs text-red-400 pl-2 flex items-center gap-1.5"
            role="alert"
          >
            {/* Pulsing warning marker */}
            <motion.span
              className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"
              animate={{ opacity: [1, 0.3, 1], scale: [1, 0.7, 1] }}
              transition={{
                duration: 1.0,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};
